import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/dashboard-pages.css';
import { getUserCredits, createCheckoutSession, redirectToCheckout } from '../services/paymentService';

const BillingPage: React.FC = () => {
  const [billingPeriod] = useState('monthly');
  const [credits, setCredits] = useState<number>(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Load credits function
  const loadCredits = React.useCallback(async () => {
    setCreditsLoading(true);
    try {
      const result = await getUserCredits();
      if (result.credits !== undefined) {
        setCredits(result.credits);
      }
      if (result.error) {
        console.error('Error loading credits:', result.error);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  // Fetch credits on mount
  useEffect(() => {
    loadCredits();
  }, [loadCredits]);
  
  // Handle payment status from URL params
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      // Reload credits after successful payment
      setTimeout(() => {
        loadCredits();
        setSearchParams({}, { replace: true }); // Clear query params without adding to history
      }, 1500);
    } else if (paymentStatus === 'cancelled') {
      setSearchParams({}, { replace: true }); // Clear query params
    }
  }, [searchParams, loadCredits, setSearchParams]);


  const handlePurchaseCredits = async (amount: number) => {
    setPurchasing(amount);
    try {
      const result = await createCheckoutSession(amount);
      if (result.url) {
        redirectToCheckout(result.url);
      } else {
        alert(result.error || 'Failed to create checkout session');
        setPurchasing(null);
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      alert('An error occurred. Please try again.');
      setPurchasing(null);
    }
  };

  const creditAmounts = [1, 10, 25, 50];

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Billing</h1>
        <p>Manage your subscription and billing information</p>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <h2>Credits</h2>
          <div className="credits-section">
            <div className="credits-balance">
              <div className="credits-label">Current Balance</div>
              <div className="credits-amount">
                {creditsLoading ? 'Loading...' : `${credits} credits`}
              </div>
            </div>
            <div className="credits-purchase">
              <div className="credits-purchase-label">Purchase Credits</div>
              <div className="credits-buttons">
                {creditAmounts.map((amount) => (
                  <button
                    key={amount}
                    className={`action-btn ${purchasing === amount ? 'loading' : 'primary'}`}
                    onClick={() => handlePurchaseCredits(amount)}
                    disabled={purchasing !== null}
                  >
                    {purchasing === amount ? 'Processing...' : `€${amount}`}
                  </button>
                ))}
              </div>
              <div className="credits-info">
                <p>1 EUR = 1 credit. Credits never expire.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Current Plan</h2>
          <div className="billing-plan">
            <div className="plan-badge">Pro Plan</div>
            <div className="plan-details">
              <div className="plan-price">
                <span className="price-amount">$29</span>
                <span className="price-period">/month</span>
              </div>
              <div className="plan-features">
                <div className="feature-item">✓ Unlimited Projects</div>
                <div className="feature-item">✓ GPU Training (100 hrs/month)</div>
                <div className="feature-item">✓ Priority Support</div>
                <div className="feature-item">✓ Advanced Analytics</div>
              </div>
            </div>
            <div className="plan-actions">
              <button className="action-btn secondary">Change Plan</button>
              <button className="action-btn secondary">Cancel Subscription</button>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Usage This Month</h2>
          <div className="usage-stats">
            <div className="usage-item">
              <div className="usage-label">GPU Hours Used</div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: '65%' }}></div>
              </div>
              <div className="usage-text">65 / 100 hours</div>
            </div>
            <div className="usage-item">
              <div className="usage-label">API Requests</div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: '42%' }}></div>
              </div>
              <div className="usage-text">42,000 / 1,000,000 requests</div>
            </div>
            <div className="usage-item">
              <div className="usage-label">Storage Used</div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: '28%' }}></div>
              </div>
              <div className="usage-text">28 / 500 GB</div>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Payment Method</h2>
          <div className="payment-method">
            <div className="card-info">
              <div className="card-icon">💳</div>
              <div className="card-details">
                <div className="card-type">Visa</div>
                <div className="card-number">•••• •••• •••• 4242</div>
                <div className="card-expiry">Expires 12/26</div>
              </div>
            </div>
            <button className="action-btn secondary">Update Payment Method</button>
          </div>
        </section>

        <section className="section-card">
          <h2>Billing History</h2>
          <div className="billing-history">
            <div className="history-item">
              <div className="history-date">Nov 1, 2025</div>
              <div className="history-description">Pro Plan - Monthly Subscription</div>
              <div className="history-amount">$29.00</div>
              <div className="history-status paid">Paid</div>
              <button className="history-btn">📥 Download</button>
            </div>
            <div className="history-item">
              <div className="history-date">Oct 1, 2025</div>
              <div className="history-description">Pro Plan - Monthly Subscription</div>
              <div className="history-amount">$29.00</div>
              <div className="history-status paid">Paid</div>
              <button className="history-btn">📥 Download</button>
            </div>
            <div className="history-item">
              <div className="history-date">Sep 1, 2025</div>
              <div className="history-description">Pro Plan - Monthly Subscription</div>
              <div className="history-amount">$29.00</div>
              <div className="history-status paid">Paid</div>
              <button className="history-btn">📥 Download</button>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Billing Plans</h2>
          <div className="plans-comparison">
            <div className="plan-card">
              <div className="plan-header">Starter</div>
              <div className="plan-price">$9<span>/mo</span></div>
              <div className="plan-list">
                <div>✓ 5 Projects</div>
                <div>✓ 10 GPU Hours/month</div>
                <div>✗ Priority Support</div>
                <div>✗ Advanced Analytics</div>
              </div>
              <button className="action-btn secondary">Select Plan</button>
            </div>
            <div className="plan-card current">
              <div className="plan-badge-current">Current</div>
              <div className="plan-header">Pro</div>
              <div className="plan-price">$29<span>/mo</span></div>
              <div className="plan-list">
                <div>✓ Unlimited Projects</div>
                <div>✓ 100 GPU Hours/month</div>
                <div>✓ Priority Support</div>
                <div>✓ Advanced Analytics</div>
              </div>
              <button className="action-btn primary">Manage Plan</button>
            </div>
            <div className="plan-card">
              <div className="plan-header">Enterprise</div>
              <div className="plan-price">Custom</div>
              <div className="plan-list">
                <div>✓ Unlimited Everything</div>
                <div>✓ Dedicated Support</div>
                <div>✓ Custom Integrations</div>
                <div>✓ SLA Guarantee</div>
              </div>
              <button className="action-btn secondary">Contact Sales</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BillingPage;
